import {
  inject,
  computed,
  signal,
  OnInit,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { OutputSettingsTransformationService } from 'src/app/core/services/output-settings-transformation.service';

import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { OutputSettingsGeneralComponent } from './output-settings-general/output-settings-general.component';
import { FeedLevelLayoutAndFormatComponent } from './feed-level-layout-and-format/feed-level-layout-and-format.component';

import {
  ItemOutputSettingsType,
  OutputSettings,
  OutputSettingsFormat,
  OutputSettingsList,
} from 'src/app/core/models/output-settings.models';
import { NomOutputFormat } from 'src/app/core/constants';

import { saveOutputSettingsSuccess } from 'src/app/core/state/output-settings.actions';
import {
  injectOutputSettingsListQuery,
  injectSaveOutputSettingsMutation,
  OutputSettingsScope,
} from 'src/app/api/queries/output-settings.queries';

import { AppState } from 'src/app/core/state/app.state';

import { startWith, tap } from 'rxjs';
import {
  DialogCloseOptions,
  DialogCloseReason,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import { TabConfig, SearchableDialog } from 'src/app/shared/utils';
import { SearchInputComponent } from '../search-input/search-input.component';
import { DialogTabNavComponent } from '../dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { SuiTooltipDirective } from '@sui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData extends TabDialogDataBase {
  outputSettings: OutputSettings;
  objectId: string;
  objectClassId: number;
}

/** The title the backend assigns to per-object (type 3) settings; never a Named List name. */
const CUSTOM_OUTPUT_SETTINGS_TITLE = '[Custom]';

/** Close data: `saved` is the in-flight save request (SavedCloseData). */
interface DialogResult {
  saved?: Promise<unknown>;
}

@Component({
  selector: 'app-output-settings',
  templateUrl: './output-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    SuiTooltipDirective,
    TranslatePipe,
  ],
})
export class OutputSettingsComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private fb = inject(FormBuilder);

  // Set in ngOnInit, once dialogData is available. The list is normally already
  // cached by the panel that opened this dialog.
  private readonly outputSettingsScope = signal<OutputSettingsScope | null>(
    null,
  );
  private readonly outputSettingsQuery = injectOutputSettingsListQuery(
    this.outputSettingsScope,
  );
  private readonly saveOutputSettingsMutation =
    injectSaveOutputSettingsMutation();

  private outputSettingsTransformationService = inject(
    OutputSettingsTransformationService,
  );

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  protected actionTitle = this.translateService.instant(
    'output-settings.title.edit',
  );
  protected objectTitle = this.translateService.instant(
    'output-settings.title.output-settings',
  );

  /**
   * Live value of the Name input. "Save as New" needs a name that differs from
   * the loaded record's: saving under the same name re-created the record as a
   * second Named List, which is how hundreds of "[Custom]"-named lists used by
   * nothing came to exist (WAP-1638).
   */
  private readonly currentTitle = signal<string>('');
  protected readonly canSaveAsNew = computed(() => {
    const title = this.currentTitle().trim();
    return (
      title !== '' &&
      title !== (this.outputSettings?.title ?? '').trim() &&
      title.toLowerCase() !== CUSTOM_OUTPUT_SETTINGS_TITLE.toLowerCase()
    );
  });

  /**
   * The loaded record is the object's own per-object settings (type 3). An
   * object has at most one, so "Save as Custom" is not offered again on it.
   */
  get isCustomRecord(): boolean {
    return (
      this.outputSettings?.output_settings_type?.id ===
      ItemOutputSettingsType.CUSTOM_OBJECT
    );
  }

  // * Central form management
  protected formGroups: FormGroup[] = [];
  private formatFormGroups: Map<NomOutputFormat, FormGroup> = new Map();

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get outputSettings(): OutputSettings {
    return this.dialogData.outputSettings;
  }

  get objectId(): string {
    return this.dialogData.objectId;
  }

  get objectClassId(): number {
    return this.dialogData.objectClassId;
  }

  ngOnInit(): void {
    this.outputSettingsScope.set({
      objectId: this.objectId,
      objectClassId: this.objectClassId,
    });

    this.initializeAllForms();
    this.subscribeToTitleChanges();

    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant('output-settings.tab.general'),
        icon: ['fas', 'gear'],
        component: OutputSettingsGeneralComponent,
        inputs: {
          outputSettings: this.outputSettings,
          formGroup: this.formGroups[0],
          searchString$: this.searchString$,
        },
      },
      {
        id: 'layout-and-format',
        label: this.translateService.instant(
          'output-settings.tab.layout-and-format',
        ),
        component: FeedLevelLayoutAndFormatComponent,
        inputs: {
          outputSettings: this.outputSettings,
          formatFormGroups: this.formatFormGroups,
          searchString$: this.searchString$,
        },
      },
    ];
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  private initializeAllForms(): void {
    this.initializeGeneralForm();
    this.initializeAllFormatForms();
  }

  private initializeGeneralForm(): void {
    const generalFormGroup = this.fb.group({
      title: [this.outputSettings?.title || ''],
      description: [this.outputSettings?.description || ''],
      timezone_id: [this.outputSettings?.timezone_id || null],
    });

    // * add to formGroups array as the first item (index 0)
    this.formGroups = [generalFormGroup];
  }

  private initializeAllFormatForms(): void {
    if (!this.outputSettings?.formats) {
      return;
    }

    // * create FormGroups for each format
    [NomOutputFormat.DOCX, NomOutputFormat.CSV, NomOutputFormat.HTML].forEach(
      (formatId) => {
        const format = this.outputSettings.formats.find(
          (f) => f.id === formatId,
        );
        if (format) {
          const formGroup = this.createFormatFormGroup(format);
          this.formatFormGroups.set(formatId, formGroup);
          this.formGroups.push(formGroup);
        }
      },
    );
  }

  private subscribeToTitleChanges(): void {
    const titleControl = this.formGroups[0]?.get('title');

    if (titleControl) {
      titleControl.valueChanges
        .pipe(
          startWith(titleControl.value || ''),
          takeUntilDestroyed(this.destroyRef),
          tap((title: string) => {
            this.objectTitle = title || '';
            this.currentTitle.set(title || '');
          }),
        )
        .subscribe();
    }
  }

  // ! Backend JSON -> Form Controls
  private createFormatFormGroup(format: OutputSettingsFormat): FormGroup {
    const context =
      this.outputSettingsTransformationService.createFeedContext();
    return this.outputSettingsTransformationService.transformApiToForm(
      format,
      context,
    );
  }

  // ! Form Controls -> Backend JSON
  populateOutputSettingsFormatFromFormGroup(
    outputSettings: OutputSettings,
    formGroup: FormGroup,
    outputFormatId: NomOutputFormat,
  ) {
    const format = outputSettings.formats.find(
      (format) => format.id === outputFormatId,
    );

    const context =
      this.outputSettingsTransformationService.createFeedContext();
    this.outputSettingsTransformationService.transformFormToApi(
      formGroup,
      format,
      context,
    );
  }

  // * Popup Action Handlers - Start
  /**
   * A snapshot of whatever list is currently cached, not a live read. Async only
   * because callers await it alongside the confirm dialogs.
   */
  private checkTitleDuplicates(excludeSettingId?: number): Promise<{
    title: string;
    type1Match: OutputSettingsList | null;
    type2Match: OutputSettingsList | null;
    type3Setting: OutputSettingsList | null;
  }> {
    const outputSettingsList = this.outputSettingsQuery.data() ?? [];

    const currentTitle =
      this.formGroups[0]?.get('title')?.value || this.outputSettings?.title;

    // ! skip empty/null titles
    if (!currentTitle?.trim()) {
      return Promise.resolve({
        title: currentTitle,
        type1Match: null,
        type2Match: null,
        type3Setting: null,
      });
    }

    const type1Match =
      outputSettingsList.find(
        (settings) =>
          settings.title === currentTitle &&
          settings.output_settings_type.id ===
            ItemOutputSettingsType.COMPANY_DEFAULT &&
          settings.id !== excludeSettingId,
      ) || null;

    const type2Match =
      outputSettingsList.find(
        (settings) =>
          settings.title === currentTitle &&
          settings.output_settings_type.id ===
            ItemOutputSettingsType.NAMED_LIST &&
          settings.id !== excludeSettingId,
      ) || null;

    const type3Setting =
      outputSettingsList.find(
        (settings) =>
          settings.output_settings_type.id ===
          ItemOutputSettingsType.CUSTOM_OBJECT,
      ) || null;

    return Promise.resolve({
      title: currentTitle,
      type1Match,
      type2Match,
      type3Setting,
    });
  }

  private showType1ConflictError(conflictTitle: string): Promise<void> {
    return new Promise((resolve) => {
      const title = this.translateService.instant(
        `output-settings.show-conflict-with-type-one.title`,
      );
      const message = this.translateService.instant(
        `output-settings.show-conflict-with-type-one.message`,
        { title },
      );

      const dialogRef = this.dialogService.openSimpleDialog(
        ConfirmDialogComponent,
        {
          data: {
            title,
            message,
            hideCancelButton: true,
            hideConfirmButton: true,
          },
          closeOnClick: false,
        },
      );

      dialogRef.closed.subscribe(() => resolve());
    });
  }

  private showType2OverwriteWarning(
    conflictTitle: string,
    usedCount: number,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const title = this.translateService.instant(
        `output-settings.show-type-two-overwrite-warning.title`,
      );
      const message = this.translateService.instant(
        `output-settings.show-type-two-overwrite-warning.message`,
        { title: conflictTitle, usedCount },
      );

      const dialogRef = this.dialogService.openSimpleDialog(
        ConfirmDialogComponent,
        {
          data: {
            title,
            message,
          },
          closeOnClick: false,
        },
      );

      dialogRef.closed.subscribe(
        (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
          resolve(event.reason === DialogCloseReason.Confirm);
        },
      );
    });
  }

  private showType3OverwriteWarning(): Promise<boolean> {
    return new Promise((resolve) => {
      const title = this.translateService.instant(
        `output-settings.show-type-three-overwrite-warning.title`,
      );
      const message = this.translateService.instant(
        `output-settings.show-type-three-overwrite-warning.message`,
      );

      const dialogRef = this.dialogService.openSimpleDialog(
        ConfirmDialogComponent,
        {
          data: {
            title,
            message,
          },
          closeOnClick: false,
        },
      );

      dialogRef.closed.subscribe(
        (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
          resolve(event.reason === DialogCloseReason.Confirm);
        },
      );
    });
  }

  private gatherFormData(): OutputSettings {
    let outputSettings = JSON.parse(JSON.stringify(this.outputSettings));

    // * process General Form
    if (this.formGroups[0]) {
      outputSettings = {
        ...outputSettings,
        ...this.formGroups[0].value,
      };
    }

    // * process Format forms (DOCX, CSV, HTML, ...)
    [NomOutputFormat.DOCX, NomOutputFormat.CSV, NomOutputFormat.HTML].forEach(
      (formatId) => {
        const formGroup = this.formatFormGroups.get(formatId);
        if (formGroup) {
          this.populateOutputSettingsFormatFromFormGroup(
            outputSettings,
            formGroup,
            formatId,
          );
        }
      },
    );

    return outputSettings;
  }

  // * Creates Type 2 - NAMED_LIST
  async saveAsNew() {
    // The button is disabled for this case; the guard covers Enter / programmatic calls.
    if (!this.canSaveAsNew()) {
      return;
    }
    const duplicates = await this.checkTitleDuplicates();

    // ! block if title matches type 1 (company default)
    if (duplicates.type1Match) {
      await this.showType1ConflictError(duplicates.title);
      return;
    }

    // ! warn if title matches type 2 (will overwrite existing type 2)
    if (duplicates.type2Match) {
      const usedCount = duplicates.type2Match.count_used || 0;
      const shouldContinue = await this.showType2OverwriteWarning(
        duplicates.title,
        usedCount,
      );
      if (!shouldContinue) {
        return;
      }
    }

    const outputSettings = this.gatherFormData();

    const updatedOutputSettings = {
      ...outputSettings,
      id: null,
      output_settings_type: {
        ...this.outputSettings.output_settings_type,
        id: ItemOutputSettingsType.NAMED_LIST,
      },
    };

    const saved = this.saveOutputSettings(updatedOutputSettings);

    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: { saved },
    });
  }

  // * Creates Type 3 - CUSTOM_OBJECT
  async saveAsCustom() {
    if (this.isCustomRecord) {
      return;
    }
    const duplicates = await this.checkTitleDuplicates();

    // ! warn if type 3 already exists (will overwrite it)
    if (duplicates.type3Setting) {
      const shouldOverwrite = await this.showType3OverwriteWarning();
      if (!shouldOverwrite) {
        return;
      }
    }

    const outputSettings = this.gatherFormData();

    const updatedOutputSettings = {
      ...outputSettings,
      id: null,
      output_settings_type: {
        ...this.outputSettings.output_settings_type,
        id: ItemOutputSettingsType.CUSTOM_OBJECT,
      },
    };

    const saved = this.saveOutputSettings(updatedOutputSettings);

    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: { saved },
    });
  }

  async editSettings() {
    const currentSettingType = this.outputSettings?.output_settings_type?.id;
    const originalTitle = this.outputSettings?.title;
    const currentTitle = this.formGroups[0]?.get('title')?.value || '';
    const titleChanged = originalTitle !== currentTitle;

    let finalSettingId = this.outputSettings.id;

    // * Type 1 (COMPANY_DEFAULT) - placeholder for future implementation
    // if (currentSettingType === ItemOutputSettingsType.COMPANY_DEFAULT) {
    //   // TODO: implement type 1 editing logic - when we have the role restrictions
    //   console.warn('Editing Type 1 settings is not yet implemented');
    //   return;
    // }

    // * Type 2 (NAMED_LIST) - check for conflicts if title changed
    if (currentSettingType === ItemOutputSettingsType.NAMED_LIST) {
      if (titleChanged) {
        const duplicates = await this.checkTitleDuplicates(
          this.outputSettings.id,
        );

        // ! block if title matches type 1 (company default)
        if (duplicates.type1Match) {
          await this.showType1ConflictError(duplicates.title);
          return;
        }

        // ! warn if title matches another type 2 (will overwrite that setting)
        if (duplicates.type2Match) {
          const usedCount = duplicates.type2Match.count_used || 0;
          const shouldContinue = await this.showType2OverwriteWarning(
            duplicates.title,
            usedCount,
          );

          if (!shouldContinue) {
            return;
          }

          // * KEY: use the matched setting's ID to overwrite it
          finalSettingId = duplicates.type2Match.id;
        }
      }
    }

    // * Type 3 (CUSTOM_OBJECT) - no checks needed, title is backend-controlled
    // * Just proceed with editing

    // * Gather all form data
    let outputSettings = this.gatherFormData();

    // * Use the final ID (either original or matched setting's ID)
    outputSettings = {
      ...outputSettings,
      id: finalSettingId,
    };

    const saved = this.saveOutputSettings(outputSettings);

    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: { saved },
    });
  }

  /**
   * The dialog closes optimistically right after this call, so `mutateAsync`
   * carries the result - per-call callbacks would be dropped on teardown.
   */
  private saveOutputSettings(
    outputSettings: OutputSettings,
  ): Promise<OutputSettings> {
    const saved = this.saveOutputSettingsMutation.mutateAsync({
      outputSettings,
      objectId: this.objectId,
      objectClassId: this.objectClassId,
    });
    saved
      .then((result) =>
        this.store.dispatch(
          saveOutputSettingsSuccess({ outputSettings: result }),
        ),
      )
      .catch(() => undefined); // ! error toast owned by the mutation hook
    return saved;
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({ reason: DialogCloseReason.Cancel });
  }
  // * Popup Action Handlers - End
}
